import { Bell, Menu, X } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useTranslation } from '../../core/hooks/useTranslation'
import { TranslationKeys } from '../../core/i18n'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import LanguageToggle from '../ui/LanguageToggle'
import UserDropdown from '../ui/UserDropdown'

interface UserHeaderProps {
  activePageName?: string
  sidebarWidth?: number // Width of sidebar in pixels (default: 256 = 64 * 4 = w-64)
  subtitle?: keyof TranslationKeys // Translation key for the subtitle
  showNotifications?: boolean
  notificationCount?: number
  onMenuClick?: () => void // For mobile hamburger menu
  sidebarOpen?: boolean // Whether sidebar is open (for close button)
}

export default function UserHeader({
  activePageName,
  sidebarWidth = 256,
  subtitle,
  showNotifications = true,
  notificationCount = 0,
  onMenuClick,
  sidebarOpen = false
}: UserHeaderProps) {
  const { t, isRTL } = useTranslation()
  const [headerMargin, setHeaderMargin] = useState(0)

  useEffect(() => {
    const updateMargin = () => {
      // On desktop (md and up), add sidebar margin
      if (window.innerWidth >= 768) {
        setHeaderMargin(sidebarWidth)
      } else {
        setHeaderMargin(0)
      }
    }

    updateMargin()
    window.addEventListener('resize', updateMargin)
    return () => window.removeEventListener('resize', updateMargin)
  }, [sidebarWidth])

  return (
    <>
      {/* Close Button for Arabic - Above Overlay on Left */}
      {isRTL && sidebarOpen && onMenuClick && (
        <button
          onClick={onMenuClick}
          className="md:hidden fixed top-3 left-3 z-[50] p-2 rounded-lg bg-white/90 hover:bg-white shadow-lg transition-all"
          aria-label={t('close') || 'Close'}
        >
          <X className="w-6 h-6 text-[#1E293B]" />
        </button>
      )}

      {/* Close Button for English - Above Overlay on Right */}
      {!isRTL && sidebarOpen && onMenuClick && (
        <button
          onClick={onMenuClick}
          className="md:hidden fixed top-3 right-3 z-[50] p-2 rounded-lg bg-white/90 hover:bg-white shadow-lg transition-all"
          aria-label={t('close') || 'Close'}
        >
          <X className="w-6 h-6 text-[#1E293B]" />
        </button>
      )}

      <header 
        className={`page-header sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-secondary-100/60 transition-all`}
        style={{ 
          [isRTL ? 'marginRight' : 'marginLeft']: `${headerMargin}px`
        }}
      >
        <div className="px-4 sm:px-6 lg:px-8 py-1">
          <div className="flex justify-between items-center h-14">
            {/* Mobile Menu Button & Page Title */}
            <div className="flex items-center gap-3 flex-1">
              {/* Hamburger Menu Button - Mobile Only */}
              {onMenuClick && !(isRTL && sidebarOpen) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onMenuClick}
                  className="md:hidden hover:bg-[#1E293B]/10 flex-shrink-0 relative z-[50]"
                  aria-label={t('menu') || 'Menu'}
                >
                  <Menu className="w-5 h-5 text-[#1E293B]" />
                </Button>
              )}
            
            <div className="flex flex-col flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#1E293B]/70">
                  {subtitle ? t(subtitle) : t('dashboard')}
                </span>
              </div>
              <span className="text-base font-semibold text-[#1E293B]">
                {activePageName || t('dashboard')}
              </span>
            </div>
          </div>
          
          {/* Header Actions */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            { false && showNotifications && (
              <div className="relative">
                <Button variant="ghost" size="sm" className="hover:bg-[#1E293B]/10">
                  <Bell className="w-4 h-4 text-[#1E293B]" />
                </Button>
                {notificationCount > 0 && (
                  <Badge 
                    variant="danger" 
                    size="sm" 
                    className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 text-xs"
                  >
                    {notificationCount}
                  </Badge>
                )}
              </div>
            )}
            
            {/* Language Toggle */}
            {/* <LanguageToggle /> */}
            
            {/* User Dropdown */}
            <div className={`${isRTL ? 'pr-3' : 'pl-3'} ${isRTL ? 'border-r' : 'border-l'} border-[#1E293B]/20`}>
              <UserDropdown />
            </div>
          </div>
        </div>
      </div>
    </header>
    </>
  )
}

